import { createContext, useContext, useEffect, useState } from "react";
import { useARC } from "providers/ARC/ARCProvider";
import { ConnectionState } from "lib/consts/ARC/connection";
import { attachRosTopicWithRetry } from "lib/ros/rosTopicSubscribeWithRetry";

export const MappingStatusContext = createContext({
    mappingEnabled: false,
    status: null,
});
MappingStatusContext.displayName = "MappingStatus";
export const useMappingStatus = () => useContext(MappingStatusContext);

const MappingStatusProvider = ({ children }) => {
    const { controlSocket, connectionState } = useARC();
    const [mappingEnabled, setMappingEnabled] = useState(false);
    const [status, setStatus] = useState(null);

    // Subscribe to /system_status topic to read mapping state
    // Topic: /system_status
    // Message type: slamware_ros_sdk/msg/SystemStatus
    // Field: mapping_enabled (boolean)
    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            // Don't reset state on disconnect - keep last known state
            // Only reset if we're explicitly disconnected (not just connecting)
            if (connectionState === ConnectionState.Disconnected) {
                setMappingEnabled(true);
                setStatus(null);
            }
            return;
        }

        const handleSystemStatus = (systemStatusMsg) => {
            try {
                if (!systemStatusMsg) {
                    return;
                }
                
                // Debug: systemStatusMsg structure:
                // {
                //   timestamp_ns: number,
                //   status: string (e.g., "DeviceRunning"),
                //   mapping_enabled: boolean
                // }
                
                // Extract mapping_enabled - check both snake_case and camelCase
                let mappingEnabledValue = systemStatusMsg.mapping_enabled;
                if (mappingEnabledValue === undefined) {
                    mappingEnabledValue = systemStatusMsg.mappingEnabled;
                }
                
                // Handle if nested in data field
                if (mappingEnabledValue === undefined && systemStatusMsg.data) {
                    mappingEnabledValue = systemStatusMsg.data.mapping_enabled || systemStatusMsg.data.mappingEnabled;
                }
                
                // Convert to boolean - explicitly handle false, true, and undefined
                let isEnabled = false;
                if (mappingEnabledValue !== undefined && mappingEnabledValue !== null) {
                    if (typeof mappingEnabledValue === 'boolean') {
                        isEnabled = mappingEnabledValue;
                    } else if (typeof mappingEnabledValue === 'number') {
                        isEnabled = mappingEnabledValue !== 0;
                    } else if (typeof mappingEnabledValue === 'string') {
                        isEnabled = mappingEnabledValue.toLowerCase() === 'true' || mappingEnabledValue === '1';
                    }
                }
                
                setMappingEnabled((prev) => {
                    return isEnabled;
                });
                
                const statusValue = systemStatusMsg.status || systemStatusMsg.data?.status || null;
                setStatus(statusValue);
            } catch (error) {
                console.error("[MappingStatus] Error processing system status:", error, systemStatusMsg);
            }
        };

        console.log("[MappingStatus] Subscribing to /system_status");
        const cleanup = attachRosTopicWithRetry(
            controlSocket,
            "/system_status",
            handleSystemStatus,
            {
                markMessageReceived: (msg) =>
                    msg != null && typeof msg === "object",
            }
        );

        return cleanup;
    }, [controlSocket, connectionState]);

    return (
        <MappingStatusContext.Provider
            value={{
                mappingEnabled,
                status,
            }}
        >
            {children}
        </MappingStatusContext.Provider>
    );
};

export default MappingStatusProvider;

